<template>
  <div class="comment-item" :class="{ 'comment-item--reply': isReply }">
    <!-- Deleted placeholder -->
    <div v-if="comment.deleted" class="comment-item__deleted">
      <i class="fas fa-ban mr-1 text-muted" />
      <span class="text-muted font-italic">{{ comment.content }}</span>
    </div>

    <!-- Normal comment -->
    <template v-else>
      <div
        class="comment-item__row"
        @mouseenter="showActions = true"
        @mouseleave="onRowLeave"
      >
        <!-- Avatar -->
        <div class="comment-item__avatar flex-shrink-0">
          <span class="symbol-label">{{ authorInitial }}</span>
        </div>

        <!-- Content -->
        <div class="comment-item__body">
          <!-- Bubble -->
          <div class="comment-item__bubble" :class="{ 'comment-item__bubble--editing': editing }">
            <div class="comment-item__header">
              <span class="comment-item__author">{{ authorName }}</span>
              <span class="comment-item__header-time">{{ formattedCreatedAt }}</span>
            </div>

            <!-- Edit mode -->
            <div v-if="editing" class="mt-1">
              <div
                ref="editTextarea"
                class="comment-item__edit-editor"
                contenteditable="true"
                @keydown.enter.exact.prevent="submitEdit"
                @keydown.esc="cancelEdit"
              />
              <div class="d-flex justify-content-end mt-1">
                <b-button size="sm" variant="light" class="mr-1" @click="cancelEdit">Huỷ</b-button>
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="saving"
                  @click="submitEdit"
                >
                  <b-spinner v-if="saving" small class="mr-1" />
                  Lưu
                </b-button>
              </div>
            </div>

            <!-- Content -->
            <div v-else-if="comment.content" class="comment-item__content">
              <span v-html="renderedContent" /><span v-if="comment.isEdited" class="comment-item__edited"> (đã chỉnh sửa)</span>
            </div>

            <!-- Attachments -->
            <div v-if="comment.attachments && comment.attachments.length" class="comment-item__attachments">
              <div class="comment-item__attachment-grid">
                <div
                  v-for="file in comment.attachments"
                  :key="file.id"
                  class="comment-item__attachment-card"
                >
                  <!-- Image preview -->
                  <template v-if="isImage(file.fileName)">
                    <div class="comment-item__attachment-img-wrap" @click="openLightbox(file)">
                      <img
                        v-if="attachmentUrls[file.id]"
                        :src="attachmentUrls[file.id]"
                        :alt="file.originalName || file.fileName"
                        class="comment-item__attachment-img"
                      />
                      <div v-else class="comment-item__attachment-img-loading">
                        <b-spinner small variant="secondary" />
                      </div>
                      <div class="comment-item__attachment-img-overlay">
                        <i class="fas fa-search-plus" />
                      </div>
                    </div>
                  </template>

                  <!-- Non-image file -->
                  <template v-else>
                    <div
                      class="comment-item__attachment-file"
                      style="cursor:pointer"
                      v-b-tooltip.hover.html.top="file.uploadedByName ? `<small>Upload bởi: ${file.uploadedByName}</small>` : (file.originalName || file.fileName)"
                      @click="previewNonImageFile(file)"
                    >
                      <i :class="getFileTypeIcon(file.fileName)" />
                      <div class="comment-item__attachment-file-info">
                        <span class="comment-item__attachment-file-name">
                          {{ file.originalName || file.fileName }}
                        </span>
                        <span v-if="file.sizeBytes" class="comment-item__attachment-file-size">
                          {{ formatSize(file.sizeBytes) }}
                        </span>
                      </div>
                      <button
                        class="comment-item__attachment-dl-btn"
                        title="Tải xuống"
                        @click.stop="downloadFile(file)"
                      >
                        <i class="fas fa-download" />
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Lightbox -->
              <vue-easy-lightbox
                esc-disabled
                :visible="lightbox.visible"
                :imgs="lightboxImages"
                :index="lightbox.index"
                @hide="lightbox.visible = false"
              />

              <!-- Preview modal cho file không phải ảnh (PDF, video, ...) -->
              <b-modal
                v-model="filePreviewModal.visible"
                :title="filePreviewModal.fileName"
                size="xl"
                hide-footer
                body-class="p-0"
                @hidden="onFilePreviewHide"
              >
                <div v-if="filePreviewModal.loading" class="text-center p-5">
                  <b-spinner variant="primary" />
                </div>
                <template v-else>
                  <div v-if="filePreviewModal.category === 'pdf'" style="height:80vh">
                    <iframe :src="filePreviewModal.url" width="100%" height="100%" style="border:none" />
                  </div>
                  <div v-else-if="filePreviewModal.category === 'video'" class="d-flex justify-content-center p-3">
                    <video :src="filePreviewModal.url" controls style="max-width:100%;max-height:70vh" />
                  </div>
                  <div v-else class="text-center p-5">
                    <i class="fas fa-file fa-4x text-secondary mb-3 d-block" />
                    <p class="text-muted">Không thể xem trước loại file này.</p>
                    <a :href="filePreviewModal.url" :download="filePreviewModal.fileName" class="btn btn-primary btn-sm">
                      <i class="fas fa-download mr-1" /> Tải về
                    </a>
                  </div>
                </template>
              </b-modal>
            </div>
          </div>

          <!-- Footer -->
          <div class="comment-item__footer">
            <span class="comment-item__time">{{ timeAgo }}</span>
            <span class="comment-item__reply-btn" @click="onReplyBtnClick">
              Trả lời
            </span>
          </div>
        </div>

        <!-- "⋯" action button — chỉ hiện nếu là chủ bình luận và không đang edit -->
        <div
          v-if="isOwner"
          v-show="(showActions || showMenu) && !editing"
          class="comment-item__more"
          @mouseenter="showActions = true"
          @click.stop="toggleMenu"
        >
          <i class="fas fa-ellipsis-h" />

          <!-- Dropdown -->
          <div v-if="showMenu" class="comment-item__dropdown" @click.stop>
            <div class="comment-item__dropdown-item" @click="onEditClick">
              <i class="fas fa-pencil-alt" />
              <span>Chỉnh sửa</span>
            </div>
            <div class="comment-item__dropdown-item comment-item__dropdown-item--danger" @click="onDeleteClick">
              <i class="fas fa-trash-alt" />
              <span>Xóa</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Replies -->
      <div v-if="comment.replies && comment.replies.length" class="comment-item__replies">
        <CommentItem
          v-for="reply in comment.replies"
          :key="reply.id"
          :comment="reply"
          :current-user-id="currentUserId"
          :module="module"
          :reference-id="referenceId"
          is-reply
          @deleted="$emit('reply-deleted', $event)"
          @edited="$emit('reply-edited', $event)"
          @mention-author="onReplyMentionAuthor"
        />
      </div>

      <!-- Inline reply input -->
      <div v-if="showReplyInput" class="comment-item__reply-input">
        <CommentInput
          :key="replyInputKey"
          :module="module"
          :reference-id="referenceId"
          :parent-id="comment.id"
          :initial-mention="replyMention"
          placeholder="Viết trả lời..."
          @submitted="onReplySubmitted"
          @cancel="showReplyInput = false"
          @error="onReplyError"
        />
      </div>
    </template>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { showDeleteAlert } from '@/utils/sweet-alert2';
import moment from 'moment';
import 'moment/locale/vi';
import axios from 'axios';
import VueEasyLightbox from 'vue-easy-lightbox';
import JwtService from '@/core/services/jwt.service';
import { cmdUrl } from '@/utils/apiUrl';
import { typecheck, getFileCategory } from '@/utils/common-upload-files';
import CommentInput from './CommentInput.vue';
import { formatDate, CLIENT_DATE_TIME_NO_SECONDS } from '@/utils/date';

export default {
  name: 'CommentItem',

  components: { CommentInput, VueEasyLightbox },

  props: {
    comment: { type: Object, required: true },
    currentUserId: { type: Number, default: null },
    module: { type: String, required: true },
    referenceId: { type: [String, Number], required: true },
    isReply: { type: Boolean, default: false },
  },

  data() {
    return {
      editing: false,
      editText: '',
      saving: false,
      showReplyInput: false,
      showActions: false,
      showMenu: false,
      replyMention: null,
      replyInputKey: 0,
      attachmentUrls: {},
      lightbox: { visible: false, index: 0 },
      filePreviewModal: { visible: false, url: '', fileName: '', category: '', loading: false },
    };
  },

  computed: {
    authorName() {
      return this.comment.author?.employee?.fullName
        || this.comment.author?.username
        || 'Người dùng';
    },
    authorInitial() {
      return this.authorName
        .split(' ')
        .filter(Boolean)
        .slice(-2)
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    },
    isOwner() {
      return this.comment.authorId === this.currentUserId;
    },
    timeAgo() {
      return moment(this.comment.createdAt).locale('vi').fromNow();
    },
    formattedCreatedAt() {
      return formatDate(this.comment.createdAt, CLIENT_DATE_TIME_NO_SECONDS);
    },
    imageAttachments() {
      return (this.comment.attachments || []).filter((f) => this.isImage(f.fileName));
    },
    lightboxImages() {
      return this.imageAttachments.map((f) => ({
        src: this.attachmentUrls[f.id] || '',
        title: f.originalName || f.fileName,
      }));
    },
    renderedContent() {
      if (!this.comment.content) return '';
      const escaped = this.comment.content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      return escaped.replace(
        /@\[([^\]]+)\]\((\d+)\)/g,
        '<span class="comment-mention">@$1</span>',
      );
    },
  },

  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    this.loadAttachmentUrls();
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },

  methods: {
    onRowLeave() {
      if (!this.showMenu) this.showActions = false;
    },

    onDocumentClick() {
      this.showMenu = false;
      this.showActions = false;
    },

    toggleMenu() {
      this.showMenu = !this.showMenu;
    },

    onEditClick() {
      this.showMenu = false;
      this.showActions = false;
      this.startEdit();
    },

    onDeleteClick() {
      this.showMenu = false;
      this.showActions = false;
      this.confirmDelete();
    },

    startEdit() {
      this.editing = true;
      this.$nextTick(() => {
        const el = this.$refs.editTextarea;
        if (!el) return;
        el.innerHTML = '';
        this.parseContentToDOM(this.comment.content, el);
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
    },

    parseContentToDOM(content, container) {
      const parts = (content || '').split(/(@\[[^\]]+\]\(\d+\))/g);
      parts.forEach((part) => {
        const match = part.match(/^@\[([^\]]+)\]\((\d+)\)$/);
        if (match) {
          const chip = document.createElement('span');
          chip.className = 'ci-mention-chip';
          chip.contentEditable = 'false';
          chip.dataset.mentionId = match[2];
          chip.dataset.mentionName = match[1];
          chip.textContent = `@${match[1]}`;
          container.appendChild(chip);
        } else if (part) {
          container.appendChild(document.createTextNode(part));
        }
      });
    },

    getEditContent() {
      const el = this.$refs.editTextarea;
      if (!el) return '';
      let content = '';
      const walk = (nodes) => {
        nodes.forEach((node) => {
          if (node.nodeType === 3) {
            content += node.textContent.replace(/\u00A0/g, ' ');
          } else if (node.nodeName === 'SPAN' && node.dataset.mentionId) {
            content += `@[${node.dataset.mentionName}](${node.dataset.mentionId})`;
          } else if (node.nodeName === 'BR') {
            content += '\n';
          } else {
            walk(Array.from(node.childNodes));
          }
        });
      };
      walk(Array.from(el.childNodes));
      return content.trim();
    },

    cancelEdit() {
      this.editing = false;
      this.editText = '';
    },

    async submitEdit() {
      const content = this.getEditContent();
      if (!content || this.saving) return;
      this.saving = true;
      try {
        const { data } = await ApiService.patch(`comments/${this.comment.id}`, {
          content,
        });
        this.$emit('edited', data.data);
        this.editing = false;
        makeToastSuccess('Đã cập nhật bình luận');
      } catch {
        makeToastFaile('Cập nhật bình luận thất bại');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete() {
      showDeleteAlert(
        'Xoá bình luận',
        'Bình luận sẽ bị xoá. Bạn có chắc không?',
        this.comment,
        () => this.deleteComment(),
      );
    },

    async deleteComment() {
      try {
        await ApiService.delete(`comments/${this.comment.id}`);
        this.$emit('deleted', this.comment.id);
        makeToastSuccess('Đã xóa bình luận');
      } catch {
        makeToastFaile('Xóa bình luận thất bại');
      }
    },

    onReplyBtnClick() {
      if (this.isReply) {
        this.$emit('mention-author', {
          id: this.comment.authorId,
          fullName: this.authorName,
        });
      } else {
        this.toggleReplyInput();
      }
    },

    toggleReplyInput() {
      if (this.showReplyInput) {
        this.showReplyInput = false;
        this.replyMention = null;
      } else {
        this.showReplyInput = true;
        this.replyMention = { id: this.comment.authorId, fullName: this.authorName };
        this.replyInputKey++;
      }
    },

    onReplyMentionAuthor(mention) {
      this.showReplyInput = true;
      this.replyMention = mention;
      this.replyInputKey++;
    },

    onReplySubmitted(reply) {
      this.$emit('reply-added', { parentId: this.comment.id, reply });
      this.showReplyInput = false;
    },

    onReplyError() {
      makeToastFaile('Gửi trả lời thất bại');
    },

    isImage(fileName) {
      return typecheck(fileName || '');
    },

    getFileTypeIcon(fileName) {
      const ext = (fileName || '').split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'fas fa-file-pdf fa-2x text-danger';
      if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word fa-2x text-primary';
      if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel fa-2x text-success';
      if (['mp4', 'mov', 'avi'].includes(ext)) return 'fas fa-file-video fa-2x text-info';
      return 'fas fa-file fa-2x text-secondary';
    },

    async loadAttachmentUrls() {
      const images = (this.comment.attachments || []).filter((f) => this.isImage(f.fileName));
      for (const file of images) {
        try {
          const resp = await axios.get(
            `${cmdUrl.File.download}?filename=${file.fileName}`,
            { responseType: 'arraybuffer', headers: { Authorization: `Bearer ${JwtService.getToken()}` } },
          );
          const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
          const url = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          this.$set(this.attachmentUrls, file.id, url);
        } catch {
          // bỏ qua file lỗi
        }
      }
    },

    openLightbox(file) {
      const idx = this.imageAttachments.findIndex((f) => f.id === file.id);
      if (idx === -1) return;
      this.lightbox = { visible: true, index: idx };
    },

    async downloadFile(file) {
      try {
        const resp = await axios.get(
          `${cmdUrl.File.download}?filename=${file.fileName}`,
          { responseType: 'blob', headers: { Authorization: `Bearer ${JwtService.getToken()}` } },
        );
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.originalName || file.fileName;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        makeToastFaile('Tải xuống thất bại');
      }
    },

    formatSize(bytes) {
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)}KB`;
      return `${(bytes / 1048576).toFixed(1)}MB`;
    },

    async previewNonImageFile(file) {
      const fileName = file.originalName || file.fileName;
      this.filePreviewModal = { visible: true, url: '', fileName, category: getFileCategory(file.fileName), loading: true };
      try {
        const resp = await axios.get(
          `${cmdUrl.File.download}?filename=${file.fileName}`,
          { responseType: 'blob', headers: { Authorization: `Bearer ${JwtService.getToken()}` } },
        );
        const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
        this.filePreviewModal.url = URL.createObjectURL(blob);
        this.filePreviewModal.loading = false;
      } catch {
        makeToastFaile('Không thể tải file để xem trước');
        this.filePreviewModal.visible = false;
      }
    },

    onFilePreviewHide() {
      if (this.filePreviewModal.url && this.filePreviewModal.url.startsWith('blob:')) {
        URL.revokeObjectURL(this.filePreviewModal.url);
      }
      this.filePreviewModal = { visible: false, url: '', fileName: '', category: '', loading: false };
    },
  },
};
</script>

<style src="./styles/comment-item.css" scoped></style>
<style src="./styles/comment-shared.css"></style>
